import { useCallback, useMemo, useState } from 'react'

import { useMutation } from '@tanstack/react-query'
import { FilterCompanyAnnualReviewWebPage } from 'commons/service/bank/companyDetails'
import { AnnualReview } from 'commons/types/DTO/bank/companyDetails/annualReview'
import { Request } from 'commons/utils/request'
import { Toast } from 'ui/components'
import { useTableState } from 'ui/components/Table'
import { useConfirmation } from 'ui/hooks'

import { handlerServerError } from '@orbit/errors'

export const useComponentsEssence = (
  companyId: number,
  filterCompanyAnnualReviewWebPage: (filter: FilterCompanyAnnualReviewWebPage) => void,
  annualReviewsTotalCount: number
) => {
  const [loading, setLoading] = useState(false)

  const defaultFilter = useMemo(
    () => ({
      relationshipId: companyId,
      page: 1,
      size: 10
    }),
    []
  )

  const [tableFilter, setTableFilter] = useState<{
    relationshipId: number
    page: number
    size: number
  }>(defaultFilter)

  const tableState = useTableState({ filter: {} })

  const changePagination = useCallback(
    async (page: number, size: number) => {
      setLoading(true)

      tableState.onPageChange(page, size)

      const filter = { ...tableFilter, page, size }

      setTableFilter(filter)

      await filterCompanyAnnualReviewWebPage(filter)

      setLoading(false)
    },
    [filterCompanyAnnualReviewWebPage, tableFilter, tableState]
  )

  const getCompanyAnnualReviewAfterDeleteWebPage = () => {
    setLoading(true)

    const isDeletingAnnualReviewLastOnPage = tableFilter.size * tableFilter.page >= annualReviewsTotalCount

    const page = isDeletingAnnualReviewLastOnPage
      ? Math.ceil((annualReviewsTotalCount - 1) / tableFilter.size) || 1
      : tableFilter.page

    filterCompanyAnnualReviewWebPage({
      ...tableFilter,
      page
    })

    tableState.onPageChange(page, tableState.size)

    const filter = { ...tableFilter, page }

    setTableFilter(filter)

    setLoading(false)
  }
  const [reviewForDelete, setReviewForDelete] = useState<AnnualReview | null>(null)

  const { mutate: deleteMutation } = useMutation<void, Error, { id: number }>({
    mutationFn: ({ id }) => Request.delete(Request.urls.bank.annualReview, { id }),
    onSuccess: async () => {
      await getCompanyAnnualReviewAfterDeleteWebPage()
    },
    onError: (error) => handlerServerError(error)
  })

  const deleteReview = useCallback(async () => {
    try {
      await deleteMutation({ id: Number(reviewForDelete?.id) || 0 })

      Toast.successSmall(`Annual Review "AR-${reviewForDelete?.id}" was successfully deleted.`)
    } catch (error) {
      handlerServerError(error)
    }
  }, [deleteMutation, reviewForDelete])

  const { Confirmation: DeleteConfirmation, open: openDeleteConfirmation } = useConfirmation({
    message: `Are you sure you want to delete "AR-${reviewForDelete?.id}" Annual Review?`,
    onConfirm: deleteReview,
    confirmationButtonText: 'Delete',
    isConfirmationNegative: true
  })

  const onDeleteReview = (review: AnnualReview) => {
    setReviewForDelete(review)
    openDeleteConfirmation()
  }

  return {
    loading,
    changePagination,
    tableState,
    onDeleteReview,
    DeleteConfirmation,
    openDeleteConfirmation,
    deleteReview
  }
}
